<template>
  <ToolShell
    tool-name="Business name generator"
    h1="Free business name generator for freelancers, studios and consultants."
    lede="Tell it what you do and pick a style. You get a list of name ideas with the matching .com to check, and one click shows more. Free, no signup, and it runs in your browser."
    :trust="['No signup', 'Unlimited ideas', 'Domain check links']"
    form-title="About your business"
    :upsell-points="[
      'Your business name on proposals, invoices and the client portal',
      'Clients sign and pay online under your business name',
      'One workspace from the first client onward',
    ]"
    free-label="The free generator"
    :free-bullets="[
      'A list of names to shortlist',
      'You check domains and trademarks yourself',
      'The name still needs a business behind it',
    ]"
    upgrade-headline="A name is the first step. Workspace369 runs the business behind it."
    :connected-bullets="[
      'Proposals and invoices carry your business name and logo',
      'Clients sign and pay online from a link',
      'Every client’s emails, files and payments in one record',
      'A 14-day free trial, then plans from $29 a month',
    ]"
    slug="/tools/business-name-generator"
    :meta-description="metaDescription"
    :doc-brand="names[0]?.name || 'Your business name'"
    :doc-subtitle="docSubtitle"
    doc-kind="NAME IDEAS"
    stamp="Ideas"
  >
    <template #form>
      <label class="ta-field">
        <span>What you do (one to three words)</span>
        <div class="ta-input">
          <input v-model="form.keywords" type="text" placeholder="brand design" maxlength="60" />
        </div>
      </label>

      <label class="ta-field">
        <span>Your name (optional)</span>
        <div class="ta-input">
          <input v-model="form.ownName" type="text" placeholder="Alex Rivera" autocomplete="name" maxlength="60" />
        </div>
      </label>

      <div class="ta-field">
        <span id="name-style-label">Style</span>
        <ToolDropdown
          v-model="form.style"
          :options="styleOptions"
          aria-label="Style"
          labelledby="name-style-label"
        />
      </div>

      <button type="button" class="ta-download" :disabled="!names.length" @click="seed += 1">
        More ideas
      </button>
      <button type="button" class="ta-download" :disabled="!names.length" @click="copyAll">
        {{ copied ? "Copied!" : "Copy the list" }}
      </button>

      <p class="ta-note">
        Before you settle on a name, check that the domain is free and search the
        <a :href="trademarkUrl" target="_blank" rel="noopener">USPTO trademark database</a>.
        To trade under it, register it with your state (an LLC or a DBA).
      </p>
    </template>

    <template #preview>
      <ol v-if="names.length" class="bn-list" translate="no">
        <li v-for="item in names" :key="item.name">
          <div>
            <strong>{{ item.name }}</strong>
            <span class="bn-kind">{{ item.kind }}</span>
          </div>
          <a :href="registrationLookupUrl(item.domain)" target="_blank" rel="noopener nofollow" class="bn-domain">
            {{ item.domain }} <span aria-hidden="true">↗</span>
          </a>
        </li>
      </ol>
      <p v-show="!names.length && !hasInput" class="ta-doc-body">Type what you do, like “brand design” or “bookkeeping”, to see name ideas.</p>
      <p v-show="!names.length && hasInput" class="ta-doc-body" role="status">Use a word with 2-20 letters (A-Z) or digits. Try “brand design”.</p>
      <p class="ta-doc-note">
        The domain link opens ICANN’s registration lookup: if a record comes back, the .com is
        already taken.
      </p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Describe the work</h2><p>One to three words about what you do. Add your name if you’d like personal-brand ideas.</p></div></div>
      <div><span>02</span><div><h2>Pick a style</h2><p>Studio, modern, classic or bold. Each mixes descriptive, evocative and invented names.</p></div></div>
      <div><span>03</span><div><h2>Shortlist and check</h2><p>Check the .com, search trademarks, and say your favorites out loud before you register one.</p></div></div>
    </template>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>Business name FAQ</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>

    <RelatedPages path="/tools/business-name-generator" title="Business name generator" />

    <Cta03 />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { generateNames, registrationLookupUrl, trademarkSearchUrl } from "~/utils/business-names.mjs";

const form = reactive({ keywords: "", ownName: "", style: "studio" });
const seed = ref(0);
const copied = ref(false);
const trademarkUrl = trademarkSearchUrl();

const styleOptions = [
  { value: "studio", label: "Studio", hint: "creative and design work" },
  { value: "modern", label: "Modern", hint: "short and casual" },
  { value: "classic", label: "Classic", hint: "consulting and advisory" },
  { value: "bold", label: "Bold", hint: "makers and builders" },
];

watch(() => [form.keywords, form.ownName, form.style], () => { seed.value = 0; });

const names = computed(() =>
  generateNames({ keywords: form.keywords, ownName: form.ownName, style: form.style, seed: seed.value }),
);

const hasInput = computed(() => Boolean(form.keywords.trim() || form.ownName.trim()));

const docSubtitle = computed(() =>
  names.value.length ? `${names.value.length} ideas · ${styleOptions.find((s) => s.value === form.style)?.label} style` : "Ideas appear as you type",
);

const copyAll = async () => {
  try {
    await navigator.clipboard.writeText(names.value.map((item) => `${item.name} (${item.domain})`).join("\n"));
    copied.value = true;
    setTimeout(() => (copied.value = false), 1600);
  } catch { /* clipboard unavailable */ }
};

const faq = [
  {
    q: "What makes a good name for a freelance or service business?",
    a: "Easy to say, easy to spell when someone has only heard it, and roomy enough for the work you will do in three years. Descriptive names tell clients what you do; evocative and invented names are easier to own as a brand and to find as a domain.",
  },
  {
    q: "Should I use my own name?",
    a: "Using your own name works well for consultants, coaches and creatives whose clients hire them personally. A separate brand name is easier to grow into a team or sell later. Many freelancers start with their name and add a studio name once they hire.",
  },
  {
    q: "How do I check if a name is available?",
    a: "Use the domain link next to each idea to see whether the .com is registered, search the USPTO trademark database for the same or similar names in your field, and search your state’s business registry. Also check the social handles you plan to use.",
  },
  {
    q: "Do I need to register my business name?",
    a: "In the US, trading under a name other than your own legal name usually requires a DBA (doing business as) filing, or you can form an LLC under the new name. Rules differ by state and country, so check with your state’s business registry.",
  },
  {
    q: "Are these names checked for trademarks?",
    a: "No. The ideas are generated from patterns and your words; they are not checked against trademarks or registrations. Always search before you register a name or print anything.",
  },
  {
    q: "Is what I type stored?",
    a: "No. Names are generated in your browser as you type, and nothing is sent to Workspace369. The domain links only open ICANN’s lookup when you click one.",
  },
];

const metaTitle = "Free Business Name Generator for Freelancers | Workspace369";
const metaDescription =
  "Free business name generator for freelancers, studios and consultants: describe your work, pick a style, and get name ideas with .com checks. No signup.";

useHead(() => ({
  title: metaTitle,
  meta: [
    { name: "description", content: metaDescription },
    { name: "twitter:title", content: metaTitle },
    { name: "twitter:description", content: metaDescription },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: metaTitle },
    { property: "og:description", content: metaDescription },
  ],
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.bn-list {
  display: grid;
  gap: 0.45rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
.bn-list li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem 0.75rem;
  padding: 0.55rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.6rem;
  background: #fff;
}
.bn-list strong {
  color: #111827;
  font-size: 0.98rem;
}
.bn-kind {
  margin-left: 0.5rem;
  color: #6b7280;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.bn-domain {
  color: #0e7490;
  font-size: 0.85rem;
  text-decoration: none;
  overflow-wrap: anywhere;
}
.bn-domain:hover {
  text-decoration: underline;
}

/* Dark mode: the site's dark tokens (page #020617, card #0f172a, hairline #243247, text #f1f5f9 / #cbd5e1 / #94a3b8, accent #67e8f9). Light styles above are unchanged. */
.dark .bn-list li {
  border: 1px solid #243247;
  background: #0f172a;
}

.dark .bn-list strong {
  color: #f1f5f9;
}

.dark .bn-kind {
  color: #cbd5e1;
}

.dark .bn-domain {
  color: #67e8f9;
}


</style>
